/**
 * Leviathan Web - Truck Form Page
 * Quasar Prime LLC
 * Tactical Industrial Design
 *
 * Create and edit trucks with capacity, compliance, and maintenance tracking
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

import { API_URL } from '@/config/api';
import { readApiFailure } from '@/utils/formErrors';

interface FormData {
  unitNumber: string;
  name: string;
  vin: string;
  licensePlate: string;
  state: string;
  year: number | null;
  make: string;
  model: string;
  color: string;
  totalCapacity: number;
  compartments: number;
  compartmentConfig: string;
  lastServiceDate: string;
  nextServiceDue: string;
  odometerReading: number | null;
  dotInspectionDate: string;
  dotInspectionDue: string;
  registrationExpiry: string;
  insuranceExpiry: string;
  insuranceCarrier: string;
  insurancePolicyNumber: string;
  notes: string;
  isActive: boolean;
}

const STATES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
];

export default function TruckFormPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<FormData>({
    unitNumber: '',
    name: '',
    vin: '',
    licensePlate: '',
    state: 'TX',
    year: null,
    make: '',
    model: '',
    color: '',
    totalCapacity: 0,
    compartments: 1,
    compartmentConfig: '',
    lastServiceDate: '',
    nextServiceDue: '',
    odometerReading: null,
    dotInspectionDate: '',
    dotInspectionDue: '',
    registrationExpiry: '',
    insuranceExpiry: '',
    insuranceCarrier: '',
    insurancePolicyNumber: '',
    notes: '',
    isActive: true,
  });

  useEffect(() => {
    if (isEditMode) {
      fetchTruck();
    }
  }, [id]);

  const fetchTruck = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/trucks/${id}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        const truck = data.truck || data;
        setFormData({
          unitNumber: truck.unitNumber || '',
          name: truck.name || '',
          vin: truck.vin || '',
          licensePlate: truck.licensePlate || '',
          state: truck.state || 'TX',
          year: truck.year,
          make: truck.make || '',
          model: truck.model || '',
          color: truck.color || '',
          totalCapacity: truck.totalCapacity || 0,
          compartments: truck.compartments || 1,
          compartmentConfig: truck.compartmentConfig || '',
          lastServiceDate: truck.lastServiceDate?.split('T')[0] || '',
          nextServiceDue: truck.nextServiceDue?.split('T')[0] || '',
          odometerReading: truck.odometerReading,
          dotInspectionDate: truck.dotInspectionDate?.split('T')[0] || '',
          dotInspectionDue: truck.dotInspectionDue?.split('T')[0] || '',
          registrationExpiry: truck.registrationExpiry?.split('T')[0] || '',
          insuranceExpiry: truck.insuranceExpiry?.split('T')[0] || '',
          insuranceCarrier: truck.insuranceCarrier || '',
          insurancePolicyNumber: truck.insurancePolicyNumber || '',
          notes: truck.notes || '',
          isActive: truck.isActive ?? true,
        });
      } else {
        setError('Truck not found');
      }
    } catch (error) {
      setError('Failed to load truck');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      const url = isEditMode ? `${API_URL}/api/trucks/${id}` : `${API_URL}/api/trucks`;

      const payload = {
        ...formData,
        lastServiceDate: formData.lastServiceDate || null,
        nextServiceDue: formData.nextServiceDue || null,
        dotInspectionDate: formData.dotInspectionDate || null,
        dotInspectionDue: formData.dotInspectionDue || null,
        registrationExpiry: formData.registrationExpiry || null,
        insuranceExpiry: formData.insuranceExpiry || null,
      };

      const response = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        navigate('/trucks');
      } else {
        /**
         * The API failure body is { success, error: { code, message, details } }
         * — there is no top-level `message`. `data.message || data.error`
         * therefore put the error OBJECT into React state, and rendering an
         * object as a child made React unmount the whole application to a
         * white page. `readApiFailure` always returns a string, and carries
         * the per-field issues alongside it.
         */
        const failure = await readApiFailure(response, 'Failed to save truck');
        setError(failure.message);
      }
    } catch (error) {
      setError('Failed to save truck');
    } finally {
      setIsSaving(false);
    }
  };

  const updateField = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">&gt; Loading truck...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {isEditMode ? 'Edit Truck' : 'New Truck'}
              </h1>
              <p className="text-text-secondary text-sm font-mono mt-1">
                {isEditMode ? 'Modify truck details' : 'Add a new vehicle to the fleet'}
              </p>
            </div>
            <button
              onClick={() => navigate('/trucks')}
              className="text-text-secondary hover:text-amber font-mono text-sm uppercase tracking-wider transition-colors"
            >
              &larr; Back to Trucks
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <Panel title="Vehicle Information">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Unit Number {!isEditMode && '(auto if blank)'}
              </label>
              <input
                type="text"
                value={formData.unitNumber}
                onChange={(e) => updateField('unitNumber', e.target.value)}
                placeholder="TRK-001"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Nickname
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="Big Red"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                VIN
              </label>
              <input
                type="text"
                value={formData.vin}
                onChange={(e) => updateField('vin', e.target.value)}
                maxLength={17}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Year
              </label>
              <input
                type="number"
                value={formData.year || ''}
                onChange={(e) => updateField('year', parseInt(e.target.value) || null)}
                min="1990"
                max="2030"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Make
              </label>
              <input
                type="text"
                value={formData.make}
                onChange={(e) => updateField('make', e.target.value)}
                placeholder="Peterbilt, Freightliner..."
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Model
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => updateField('model', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Color
              </label>
              <input
                type="text"
                value={formData.color}
                onChange={(e) => updateField('color', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                License Plate
              </label>
              <input
                type="text"
                value={formData.licensePlate}
                onChange={(e) => updateField('licensePlate', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                State
              </label>
              <select
                value={formData.state}
                onChange={(e) => updateField('state', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              >
                {STATES.map((st) => (
                  <option key={st} value={st}>
                    {st}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Panel>

        {/* Capacity */}
        <Panel title="Capacity & Configuration">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Total Capacity (gallons)
              </label>
              <input
                type="number"
                value={formData.totalCapacity || ''}
                onChange={(e) => updateField('totalCapacity', parseInt(e.target.value) || 0)}
                min="0"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                # of Compartments
              </label>
              <input
                type="number"
                value={formData.compartments || ''}
                onChange={(e) => updateField('compartments', parseInt(e.target.value) || 1)}
                min="1"
                max="10"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Compartment Config
              </label>
              <input
                type="text"
                value={formData.compartmentConfig}
                onChange={(e) => updateField('compartmentConfig', e.target.value)}
                placeholder="2500/2500/3000"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* Maintenance & Compliance */}
        <Panel title="Maintenance & Compliance">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Odometer
              </label>
              <input
                type="number"
                value={formData.odometerReading || ''}
                onChange={(e) => updateField('odometerReading', parseInt(e.target.value) || null)}
                min="0"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Last Service
              </label>
              <input
                type="date"
                value={formData.lastServiceDate}
                onChange={(e) => updateField('lastServiceDate', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Next Service Due
              </label>
              <input
                type="date"
                value={formData.nextServiceDue}
                onChange={(e) => updateField('nextServiceDue', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                DOT Inspection Date
              </label>
              <input
                type="date"
                value={formData.dotInspectionDate}
                onChange={(e) => updateField('dotInspectionDate', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                DOT Inspection Due
              </label>
              <input
                type="date"
                value={formData.dotInspectionDue}
                onChange={(e) => updateField('dotInspectionDue', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Registration Expiry
              </label>
              <input
                type="date"
                value={formData.registrationExpiry}
                onChange={(e) => updateField('registrationExpiry', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Insurance Expiry
              </label>
              <input
                type="date"
                value={formData.insuranceExpiry}
                onChange={(e) => updateField('insuranceExpiry', e.target.value)}
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Insurance Carrier
              </label>
              <input
                type="text"
                value={formData.insuranceCarrier}
                onChange={(e) => updateField('insuranceCarrier', e.target.value)}
                placeholder="e.g., Progressive Commercial"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Insurance Policy Number
              </label>
              <input
                type="text"
                value={formData.insurancePolicyNumber}
                onChange={(e) => updateField('insurancePolicyNumber', e.target.value)}
                placeholder="e.g., POL-2024-00123"
                className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
              />
            </div>
          </div>
        </Panel>

        {/* Notes & Status */}
        <Panel title="Notes">
          <textarea
            value={formData.notes}
            onChange={(e) => updateField('notes', e.target.value)}
            rows={3}
            className="w-full bg-background border border-panel-border focus:border-amber focus:ring-1 focus:ring-amber px-4 py-2 text-text-primary font-mono text-sm"
          />

          <div className="mt-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => updateField('isActive', e.target.checked)}
                className="w-4 h-4 bg-background border border-panel-border text-amber focus:ring-amber"
              />
              <span className="text-text-secondary font-mono text-sm">
                Active (available for dispatch)
              </span>
            </label>
          </div>
        </Panel>

        {/* Actions */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/trucks')}
            className="bg-panel-border hover:bg-steel/20 text-text-primary font-mono py-3 px-8 transition-all uppercase tracking-wider"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="bg-amber hover:bg-amber/90 text-background font-display font-bold py-3 px-8 transition-all uppercase tracking-wider disabled:opacity-50"
            style={{ boxShadow: '0 0 20px rgba(255, 149, 0, 0.4)' }}
          >
            {isSaving ? 'Saving...' : isEditMode ? 'Update Truck' : 'Add Truck'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <h2 className="text-lg font-display text-text-primary uppercase tracking-wide mb-4 pb-3 border-b border-amber">
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}
